import { View } from "@/components/ui/view";
import { Text } from "@/components/ui/text";
import { Divider } from "@/components/ui/divider";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18n from "i18n-js";
import { FlatList } from 'react-native-gesture-handler';
import Footer from "@/components/basic/Footer";
import { useDispatch, useSelector } from 'react-redux';
import { backendRequest } from "@/stores/thunks";
import ContactElement from "@/components/basic/ContactElement";
import ModalConfirm from "@/components/basic/ModalConfirm";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { switchScreen } from '@/hooks/navigate';

export default function ShareWorkflow({ navigation, route }) {
    const { task, info, isEdit, isFinal, isCopy } = route.params
    const contacts = useSelector((state) => state.contacts)
    const server = useSelector((state) => state.server)
    const dispatch = useDispatch()
    const insets = useSafeAreaInsets()

    const initialContacts = useRef(isCopy ? [] : (contacts.tasks2shares[task.id]?.contacts || []))

    const initialChannels = useRef(isCopy ? [] : (contacts.tasks2shares[task.id]?.channels || []))

    const [actContacts, setActContacts] = useState(initialContacts.current)
    const [actChannels, setActChannels] = useState(initialChannels.current)

    const sameSet = useCallback((a = [], b = []) => {
        if (a.length !== b.length) return false
        const s = new Set(a)
        return b.every(x => s.has(x))
    }, [])


    const dirty =
        !sameSet(actContacts, initialContacts.current) ||
        !sameSet(actChannels, initialChannels.current)

    const channelContacts = useMemo(() => {
        return contacts.channels
                        .filter(channel => actChannels.includes(channel.id))
                        .flatMap(channel => channel.contacts ?? [])
    }, [contacts.channels, actChannels])

    const removedContacts = useMemo(() => {
        return initialContacts.current.filter(
            id => !actContacts.includes(id)
        )
    }, [actContacts])

    const removedChannels = useMemo(() => {
        return initialChannels.current.filter(
            id => !actChannels.includes(id)
        )
    }, [actChannels])

    const hasRemovals =
        removedContacts.length > 0 ||
        removedChannels.length > 0

    const [showModal, setShowModal] = useState(false)

    const setContacts = (contactId, isSelected) => {
        // since isSelected reflects a combination of contact and channels state we need check contacts directly
        if (!actContacts.includes(contactId)) {
            setActContacts(prev => {
                const next = [...prev]
                next.push(contactId)
                return next
            })
        } else {
            setActContacts(prev => {
                return prev.filter(id => id !== contactId)
            })
        }
    }

    const showContacts = (channelId) => {
        const channel = contacts.channels.find(channel => channel.id === channelId)
        channel && switchScreen(navigation, 'Contacts', { channel: channel, isFinal: true })
    }

    const setChannels = (channelId, isSelected) => {
        if (isSelected) {
            setActChannels(prev => {
                const next = [...prev]
                next.push(channelId)
                return next
            })
        } else {
            setActChannels(prev => {
                return prev.filter(id => id !== channelId)
            })
        }
    }

    function saveShare() {
        const oldContacts = initialContacts.current
        const oldChannels = initialChannels.current

        const addContacts = actContacts.filter(c => !oldContacts.includes(c))
        const delContacts = oldContacts.filter(c => !actContacts.includes(c))

        const addChannels = actChannels.filter(c => !oldChannels.includes(c))
        const delChannels = oldChannels.filter(c => !actChannels.includes(c))

        if (
            addContacts.length === 0 &&
            delContacts.length === 0 &&
            addChannels.length === 0 &&
            delChannels.length === 0
        ) {
            return
        }

        dispatch(backendRequest(
            'SHARE_WORKFLOW_INLINE',
            `/share/${task.workflowId}`,
            {
                contacts: addContacts,
                channels: addChannels,
                delContacts,
                delChannels
            },
            'PUT',
            false
        ))
    }

    function sendCopy() {
        const users = [
            ...actContacts,
            ...channelContacts.filter(id => !actContacts.includes(id))
        ]
        .map(id => contacts.active.find(c => c.id === id)?.toUser?.id)
         .filter(Boolean)

        if (users.length === 0) return

        dispatch(backendRequest(
            'SEND_WORKFLOW_INLINE',
            `/workflow/${task.workflowId}/send`,
            {users},
            'POST',
            false
        ))
    }

    const onSaveShare = () => {
        if (dirty && hasRemovals) {
            setShowModal(true)
        } else {
            if (dirty){
                saveShare()
            }
            switchScreen(navigation, 'BACK', { isEdit: false })
        }
    }

    const onSendCopy = () => {
        sendCopy()
        switchScreen(navigation, 'BACK')
    }

    useEffect(() => {
        if (info) {
            navigation.setOptions({ info: info })
        }
    }, [])


    const actions = [
        { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        isEdit ?
            (isCopy ? { icon: 'send', text: 'footer.send', exec: onSendCopy }
                    : { icon: 'check', text: 'footer.finish', exec: onSaveShare}) :
            (server.online && !isFinal ? { icon: 'edit', text: 'footer.edit', component: 'ShareWorkflow', params: { task: task, info: info, isEdit: true } }
                                       : { icon: '' })
    ]

    const closeModal = (confirmed) => {
        setShowModal(false)
        if (confirmed) {
            saveShare()
            switchScreen(navigation, 'BACK', { isEdit: false })

        } else {
            // reset selections
            setActContacts(initialContacts.current)
            setActChannels(initialChannels.current)
        }
    }

    const renderItem = ({ item, index }) => {
        return (item.info ? <>
            {item.div && <Divider className="mt-2" />}
            <Text className="px-3 py-3 text-2xl text-secondary-900">{item.info}</Text>
        </> : item.toUser ?
            <ContactElement
                key={'A' + index}
                uri={item?.contactImage?.id || item.toUser.id}
                title={item.contactAlias || item.toUser.mail} text={item.toUser.preferredName}
                onPress={isEdit ? setContacts : null}
                id={item.id}
                selected={isEdit ? actContacts.includes(item.id) : null}
                selected2={isEdit ? channelContacts.includes(item.id) : null} />
            :
            <ContactElement
                key={'B' + index}
                uri={item?.channelImage?.id}
                title={item.name}
                text={i18n.t('channel.numcontacts', { num: item.contacts.length })}
                onPress={isEdit ? setChannels : showContacts}
                id={item.id}
                selected={isEdit ? actChannels.includes(item.id) : null} />);
    }

    const getData = () => {
        const showContacts = contacts.active.filter(contact => isEdit || actContacts.includes(contact.id))
        const showChannels = contacts.channels.filter(channel => isEdit || actChannels.includes(channel.id))
        return [{ info: i18n.t('contactManagement.contacts', { num: showContacts.length }) }, ...showContacts, { info: i18n.t('contactManagement.channels', { num: showChannels.length }), div: true }, ...showChannels]
    }

    return (
        <View className="flex-1 bg-secondary-50">
            <FlatList
                contentContainerStyle={{ paddingBottom: 70 + insets.bottom }}
                data={getData()}
                renderItem={renderItem}
                keyExtractor={(item, index) => (item.id || 'T') + index}
            />
            <ModalConfirm showModal={showModal && server.online} closeModal={closeModal} title='shareWorkflow.title' text='shareWorkflow.textTaskUnshare' buttonOk='shareWorkflow.ok' />
            <Footer navigation={navigation} actions={actions} />
        </View>
    );
}